/**
 * How the ADMIN tRPC client (`lib/trpc.ts`'s `createTrpcClient`) should carry
 * the WagePro session on a given runtime.
 *
 * Pure and Capacitor-free on purpose — no `@capacitor/*` import, no
 * `import.meta.env` read — so the whole decision is unit-testable in
 * `bun:test` without a bridge or a DOM, exactly like `api_target.ts`'s
 * resolvers. The runtime snapshot is supplied by the caller.
 *
 * Two transports, one per runtime:
 *
 *  - **Web** — `credentials: 'include'` (WagePro's OWN session cookie is
 *    honoured whenever it is same-site or partitioned), plus
 *    `Authorization: Bearer <oauth token>` ONLY when the caller supplies
 *    a non-empty {@link AdminTransportInput.oauthToken}. The admin tRPC
 *    client in `lib/trpc.ts` gates that supply behind a runtime flag
 *    (`setAdminBearerFallback`, off by default): the bearer is attached
 *    only while a bearer-backed Ledger-grant session is active — the
 *    Ledger-SSO cross-site handoff (#25227), where `app.wagepro.ai` and
 *    `api.ledgerpro.ai` are different registrable domains and the Ledger
 *    session cookie (`SameSite=Lax`) is never sent. In every other
 *    topology (same-site prod, local dev, a plain WagePro-first cookie
 *    login, boot's own `auth.me` cookie cross-check) the caller passes
 *    `oauthToken: null` and the browser behaviour is byte-identical to
 *    before #25227. `apps/api/src/trpc/context.ts` resolves a cookie
 *    session BEFORE the bearer, so any topology where the cookie IS
 *    reachable is unchanged.
 *  - **Native shell** — `Authorization: Bearer <session id>` and
 *    `credentials: 'omit'`. The shell's WebView origin
 *    (`https://app.wagepro.ai`) and the API (`https://api.ledgerpro.ai`) are
 *    different registrable domains, so the `SameSite=Lax` cookie is never
 *    sent and cookie transport simply cannot work there. Unlike the web
 *    branch above, this bearer is the raw WagePro session id, not an
 *    OAuth token — see `docs/adr/20260908_01_wagepro-native-shell-session-bearer.md`.
 *
 * The `'omit'` is LOAD-BEARING, not tidiness — same rationale as
 * `apps/costpro-web/src/lib/trpc_bearer.ts`. `apps/api`'s `createContext`
 * resolves a cookie session BEFORE the Bearer token (`if (!sessionId &&
 * bearerToken) sessionId = bearerToken`, `apps/api/src/trpc/context.ts`), so
 * ANY tag-along cookie shadows the bearer entirely. Left at fetch's default
 * (`same-origin`), a local shell build pointed at a same-origin dev API — or
 * any future same-site deployment — would silently authenticate as whatever
 * stale `mukil_session` the WebView happens to hold, instead of the session
 * this shell just stored. That failure is invisible: it authenticates
 * SUCCESSFULLY, as the wrong session. Omitting cookies makes the bearer the
 * only credential on the wire, so the transport the tests pin is the
 * transport that runs.
 */

/** The runtime facts {@link resolveAdminTransport} decides from. */
export interface AdminTransportInput {
  /** Running inside the native Capacitor shell (`lib/platform.ts`'s `isNative()`). */
  native: boolean;
  /** The stored native-shell session id, if any. `null` on web, and on native before sign-in. */
  token: string | null;
  /**
   * Web-only: the WagePro-audience OAuth access token from
   * `lib/auth/oauth_pkce.ts`'s `getValidToken`, or `null` when none is
   * stored / not yet a Ledger-SSO session. Ignored on native (which uses
   * {@link token} instead). Load-bearing for the production `app.wagepro.ai`
   * → `api.ledgerpro.ai` cross-site case (#25227) where the Ledger
   * `SameSite=Lax` cookie is withheld and the bearer is the only
   * credential the API can resolve the caller from.
   */
  oauthToken: string | null;
}

/** The fetch-level transport the tRPC `httpLink` should apply. */
export interface AdminTransport {
  credentials: RequestCredentials;
  headers: Record<string, string>;
}

/**
 * Resolve the admin-console transport for one runtime.
 *
 * On native with no stored token the headers are empty and credentials stay
 * `'omit'`: the request goes out unauthenticated and the API returns a clean
 * 401, which the boot path already renders as "signed out". Falling back to
 * `'include'` there would be worse than useless — the cookie cannot be sent
 * cross-site anyway, and it would reintroduce exactly the shadowing hazard
 * the module docblock describes.
 */
export function resolveAdminTransport(
  input: AdminTransportInput,
): AdminTransport {
  if (!input.native) {
    return {
      credentials: 'include',
      headers: input.oauthToken
        ? { Authorization: `Bearer ${input.oauthToken}` }
        : {},
    };
  }
  return {
    credentials: 'omit',
    headers: input.token ? { Authorization: `Bearer ${input.token}` } : {},
  };
}
